import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { Button, Empty, Popconfirm, Spin, Tooltip, Typography } from 'antd';
import {
  ArrowUpOutlined,
  MenuOutlined,
} from '@ant-design/icons';

import {
  askCodiAnalytics,
  deleteCodiConversation,
  loadCodiConversations,
  openCodiConversation,
  startNewCodiAnalyticsChat,
} from '../../../store/codiAnalytics';
import { CodiAnalyticsFollowUps, CodiAnalyticsResult } from '../../../components/codi/CodiAnalyticsResult';
import { DeleteIcon, MedHaulMark } from '../../../assets';
import chatIcon from '../../../assets/svgs/chatIcon.svg';
import codiExpandedIcon from '../../../assets/svgs/codiExpanded.svg';
import medhaulBlackIcon from '../../../assets/svgs/MedhaulBlackIcon.png';
import recentRidesIcon from '../../../assets/svgs/CodiRecentRide.svg';
import rideStatusIcon from '../../../assets/svgs/CodiRideStatus.svg';
import monthlyMileageIcon from '../../../assets/svgs/CodiMonthlyMileage.svg';
import last100RidesIcon from '../../../assets/svgs/Codi100Rides.svg';

import { USER_ROLES } from '../../../constants/constants';
import { DateHelper } from '../../../utils';
import styles from './index.module.css';

const { Text } = Typography;

// Short chip labels keep the hero calm; the agent still receives the long,
// unambiguous prompt.
const MH_SUGGESTIONS = [
  {
    label: 'Recent Rides',
    icon: recentRidesIcon,
    prompt: 'Show the most recent rides with their current status',
  },
  {
    label: 'Ride Status',
    icon: rideStatusIcon,
    prompt:
      'How many rides were completed vs cancelled in the last 30 days? Break it down by day.',
  },
  {
    label: 'Avg Monthly Mileage',
    icon: monthlyMileageIcon,
    prompt: 'What is the average mileage per ride by month this year?',
  },
  {
    label: 'Last 100 rides',
    icon: last100RidesIcon,
    prompt: 'Show me the last 100 rides with pickup locations and completion status',
  },
];

const HO_SUGGESTIONS = [
  {
    label: 'Recent Rides',
    icon: recentRidesIcon,
    prompt: 'Show the most recent rides with their current status',
  },
  {
    label: 'Ride Status',
    icon: rideStatusIcon,
    prompt:
      'How many rides were completed vs cancelled in the last 30 days? Break it down by day.',
  },
  {
    label: 'Avg Monthly Mileage',
    icon: monthlyMileageIcon,
    prompt: 'What is the average mileage per ride by month this year?',
  },
  {
    label: 'Last 100 rides',
    icon: last100RidesIcon,
    prompt: 'Show me the last 100 rides with pickup locations and completion status',
  },
];

// const HO_CODI_ROLES = new Set([USER_ROLES.HO_ADMIN, USER_ROLES.ENTERPRISE_ADMIN]);
const HO_CODI_ROLES = new Set([USER_ROLES.HO_ADMIN]);

const MAX_INPUT_HEIGHT = 180;
const SIDEBAR_STORAGE_KEY = 'codi.analytics.sidebarOpen';

// TEMP (testing): single prefix so these are easy to filter in the console and
// easy to strip out later.
const log = (...args) => console.log('[Codi_Analytics]', ...args);

// Rail collapse survives navigation — it reads as a workspace preference, not a
// per-visit toggle.
const readSidebarPref = () => {
  try {
    return window.localStorage.getItem(SIDEBAR_STORAGE_KEY) !== 'false';
  } catch {
    return true;
  }
};

const greetingForNow = (timezone) => {
  const now = timezone
    ? DateHelper.utcToZonedTime(new Date(), timezone)
    : new Date();
  const hour = DateHelper.getHoursNumber(now);
  if (hour < 12) return 'Good Morning';
  if (hour < 18) return 'Good Afternoon';
  return 'Good Evening';
};

// Compact "2h" / "3d" style stamp, used as the row tooltip.
const formatRelativeTime = (epochSeconds) => {
  if (!epochSeconds) return '';
  const diff = Date.now() / 1000 - epochSeconds;
  if (diff < 60) return 'now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(epochSeconds * 1000).toLocaleDateString();
};

const startOfDay = (date) => {
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  return copy;
};

const bucketFor = (epochSeconds) => {
  if (!epochSeconds) return 'Older';
  const days = Math.round(
    (startOfDay(new Date()) - startOfDay(new Date(epochSeconds * 1000))) / 86400000,
  );
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days <= 7) return 'Previous 7 days';
  return 'Older';
};

// Group the history rail by recency, preserving the newest-first order the API
// already returns.
const groupConversations = (conversations) => {
  const groups = [];
  conversations.forEach((conv) => {
    const label = bucketFor(conv.updatedAt || conv.createdAt);
    const last = groups[groups.length - 1];
    if (last && last.label === label) last.items.push(conv);
    else groups.push({ label, items: [conv] });
  });
  return groups;
};

/**
 * Codi AI — natural-language analytics over BigQuery for MH Admin and HO Admin.
 *
 * Two panes: a collapsible conversation-history rail on the left and the chat on
 * the right. The chat opens on a branded hero with a single search bar —
 * deliberately minimal: no file attachments and no model picker, one input, one
 * agent.
 */
const Codi_Analytics = ({
  role,
  loading,
  threadLoading,
  messages,
  conversationId,
  conversations,
  conversationsLoading,
  preferredTimezone,
  askCodiAnalytics,
  startNewCodiAnalyticsChat,
  loadCodiConversations,
  openCodiConversation,
  deleteCodiConversation,
}) => {
  const [value, setValue] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(readSidebarPref);
  const inputRef = useRef(null);
  const bottomRef = useRef(null);

  const hasConversation = messages.length > 0;
  // Keep the chat layout up (not the hero) while a thread is being fetched.
  const showConversation = hasConversation || threadLoading;
  const busy = loading || threadLoading;

  const conversationGroups = useMemo(
    () => groupConversations(conversations),
    [conversations],
  );

  const suggestions = useMemo(
    () => (HO_CODI_ROLES.has(role) ? HO_SUGGESTIONS : MH_SUGGESTIONS),
    [role],
  );

  useEffect(() => {
    // Entering the screen always starts on the hero: a thread left open from an
    // earlier visit must not persist across navigation. Reset first, then load
    // the history rail fresh.
    log('mount → reset chat + load conversations');
    startNewCodiAnalyticsChat();
    loadCodiConversations();
  }, [startNewCodiAnalyticsChat, loadCodiConversations]);

  // TEMP (testing): snapshot of every prop that drives what the pane renders.
  useEffect(() => {
    log('state', {
      loading,
      threadLoading,
      conversationsLoading,
      conversationId,
      messageCount: messages.length,
      conversationCount: conversations.length,
      view: showConversation ? 'conversation' : 'hero',
    });
  }, [
    loading,
    threadLoading,
    conversationsLoading,
    conversationId,
    messages,
    conversations,
    showConversation,
  ]);

  // TEMP (testing): what actually came back from the agent, per message.
  useEffect(() => {
    log('messages', messages);
  }, [messages]);

  const toggleSidebar = useCallback(() => {
    setSidebarOpen((open) => {
      const next = !open;
      try {
        window.localStorage.setItem(SIDEBAR_STORAGE_KEY, String(next));
      } catch {
        /* private mode — the toggle still works for this session */
      }
      return next;
    });
  }, []);

  const resizeInput = useCallback(() => {
    const node = inputRef.current;
    if (!node) return;
    node.style.height = 'auto';
    node.style.height = `${Math.min(node.scrollHeight, MAX_INPUT_HEIGHT)}px`;
  }, []);

  useEffect(() => {
    resizeInput();
  }, [value, resizeInput]);

  useEffect(() => {
    if (hasConversation) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [messages.length, loading, hasConversation]);

  // The composer moves between the hero and the docked layout, so re-focus it
  // once an answer lands to keep follow-up questions one keystroke away.
  useEffect(() => {
    if (!loading) inputRef.current?.focus();
  }, [loading, hasConversation]);

  const submit = useCallback(
    (question) => {
      const prompt = (question ?? value).trim();
      if (!prompt || busy) {
        log('submit ignored', { prompt, busy, reason: !prompt ? 'empty' : 'busy' });
        return;
      }
      log('submit', { prompt, from: question ? 'suggestion' : 'input' });
      setValue('');
      askCodiAnalytics(prompt);
    },
    [value, busy, askCodiAnalytics],
  );

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  const composer = useMemo(
    () => (
      <div className={styles.composer}>
        <div className={styles.searchBar}>
          <textarea
            ref={inputRef}
            className={styles.input}
            rows={1}
            value={value}
            placeholder='Ask Codi a question....'
            disabled={busy}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          {/* Stays brand-green on an empty box, as designed — antd's disabled
              styling would wash it out, so it is only disabled while busy and
              `submit` ignores an empty prompt. */}
          <Tooltip title='Send (Enter)'>
            <span className={styles.sendBtnWrap}>
              <Button
                type='primary'
                shape='circle'
                className={styles.sendBtn}
                icon={<ArrowUpOutlined />}
                disabled={busy}
                onClick={() => submit()}
              />
            </span>
          </Tooltip>
        </div>
        <Text type='secondary' className={styles.hint}>
          Enter to send · Shift+Enter for a new line · Context-aware within this chat
        </Text>
      </div>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [value, busy, submit],
  );

  const railToggle = (
    <Tooltip title={sidebarOpen ? 'Hide chats' : 'Show chats'}>
      <button
        type='button'
        className={classnames(styles.railToggle, { [styles.railToggleOpen]: sidebarOpen })}
        aria-label={sidebarOpen ? 'Hide chats' : 'Show chats'}
        onClick={toggleSidebar}>
        {sidebarOpen ? (
          <img src={codiExpandedIcon} alt='' width={40} height={40} />
        ) : (
          <MenuOutlined className={styles.railToggleMenu} />
        )}
      </button>
    </Tooltip>
  );

  const codiBrand = (
    <div className={styles.conversationBrand}>
      <img
        src={medhaulBlackIcon}
        alt=''
        role='presentation'
        className={styles.conversationBrandIcon}
      />
      <span className={styles.conversationBrandName}>Codi</span>
      <span className={styles.betaBadge}>BETA</span>
    </div>
  );

  const historyRail = (
    <aside className={styles.sidebar}>
      <button
        type='button'
        className={styles.newChat}
        disabled={busy}
        onClick={startNewCodiAnalyticsChat}>
        <img src={chatIcon} alt='' className={styles.newChatIcon} />
        <span className={styles.newChatLabel}>New chat</span>
      </button>

      <div className={styles.convList}>
        {conversationsLoading && !conversations.length ? (
          <div className={styles.convLoading}>
            <Spin size='small' />
          </div>
        ) : conversations.length ? (
          conversationGroups.map((group) => (
            <div key={group.label} className={styles.convGroup}>
              <div className={styles.convGroupLabel}>{group.label}</div>
              {group.items.map((conv) => (
                <div
                  key={conv.id}
                  className={classnames(styles.convItem, {
                    [styles.convItemActive]: conv.id === conversationId,
                  })}>
                  <div
                    role='button'
                    tabIndex={0}
                    title={formatRelativeTime(conv.updatedAt || conv.createdAt)}
                    className={styles.convOpen}
                    onClick={() => {
                      log('open conversation', { id: conv.id, title: conv.title, busy });
                      if (!busy) openCodiConversation(conv.id);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !busy) {
                        log('open conversation (Enter)', conv.id);
                        openCodiConversation(conv.id);
                      }
                    }}>
                    {threadLoading && conv.id === conversationId && (
                      <Spin size='small' className={styles.convSpinner} />
                    )}
                    <span className={styles.convTitle}>{conv.title || 'New chat'}</span>
                  </div>
                  <Popconfirm
                    title='Delete this chat and its history?'
                    okText='Delete'
                    okButtonProps={{ danger: true }}
                    cancelText='Cancel'
                    disabled={loading && conv.id === conversationId}
                    onConfirm={() => {
                      log('delete conversation (rail)', conv.id);
                      return deleteCodiConversation(conv.id);
                    }}>
                    <button
                      type='button'
                      className={styles.convDelete}
                      aria-label='Delete chat'
                      disabled={loading && conv.id === conversationId}>
                      <DeleteIcon width={18} height={18} />
                    </button>
                  </Popconfirm>
                </div>
              ))}
            </div>
          ))
        ) : (
          <Empty
            className={styles.convEmpty}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description='No conversations yet'
          />
        )}
      </div>
    </aside>
  );

  return (
    <div className={styles.page}>
      <div className={classnames(styles.mainPane, { [styles.mainPaneWide]: !sidebarOpen })}>
        <div className={styles.mainHead}>
          <div className={styles.mainHeadStart}>
            {codiBrand}
          </div>
          {railToggle}
        </div>

        {!showConversation ? (
          <div className={styles.hero}>
            {/* Own layer so the glow is clipped without clipping the content —
                the hero itself has to stay scrollable on short viewports. */}
            <div className={styles.heroGlow} aria-hidden='true' />
            <div className={styles.heroInner}>
              <div className={styles.brandMark}>
                <MedHaulMark className={styles.brandMarkIcon} fill='#fff' size={54} />
              </div>
              <h1 className={styles.heroTitle}>{greetingForNow(preferredTimezone)}</h1>
              <p className={styles.heroSubtitle}>
                Ask me anything about your rides, patients,
                <br />
                providers, or daily operations.
              </p>

              <div className={styles.suggestions}>
                {suggestions.map((suggestion) => (
                  <button
                    key={suggestion.label}
                    type='button'
                    title={suggestion.prompt}
                    className={styles.suggestion}
                    disabled={loading}
                    onClick={() => submit(suggestion.prompt)}>
                    <img src={suggestion.icon} alt='' className={styles.suggestionIcon} />
                    <span>{suggestion.label}</span>
                  </button>
                ))}
              </div>

              {composer}
            </div>
          </div>
        ) : (
          <div className={styles.conversation}>
            <div className={styles.thread}>
              <div className={styles.threadInner}>
                {threadLoading ? (
                  <div className={styles.threadLoading}>
                    <Spin />
                    <span>Loading conversation…</span>
                  </div>
                ) : (
                  messages.map((msg) =>
                    msg.role === 'user' ? (
                      <div key={msg.id} className={styles.question}>
                        {msg.text}
                      </div>
                    ) : (
                      <div key={msg.id} className={styles.answerRow}>
                        <div className={styles.codiAvatar} aria-hidden='true'>
                          <MedHaulMark fill='#fff' size={24} />
                        </div>
                        <div className={styles.answerCol}>
                          <div className={styles.answerBubble}>
                            <CodiAnalyticsResult
                              result={msg.result}
                              error={msg.error}
                            />
                          </div>
                          <CodiAnalyticsFollowUps
                            result={msg.result}
                            onSelect={submit}
                            disabled={busy}
                          />
                        </div>
                      </div>
                    ),
                  )
                )}

                {loading && !threadLoading && (
                  <div className={styles.thinking} aria-live='polite'>
                    <div className={styles.codiAvatar} aria-hidden='true'>
                      <MedHaulMark fill='#fff' size={24} />
                    </div>
                    <div className={styles.thinkingContent}>
                      <div className={styles.thinkingBubble}>Let me check that quickly....</div>
                      <div className={styles.thinkingDots} aria-hidden='true'>
                        <span />
                        <span />
                        <span />
                      </div>
                    </div>
                  </div>
                )}

                <div ref={bottomRef} />
              </div>
            </div>

            <div className={styles.dockedComposer}>{composer}</div>
          </div>
        )}
      </div>
      {sidebarOpen && historyRail}
    </div>
  );
};

const mapStateToProps = (state) => ({
  role: state.authorization?.role,
  loading: state.codiAnalytics.loading,
  threadLoading: state.codiAnalytics.threadLoading,
  messages: state.codiAnalytics.messages,
  conversationId: state.codiAnalytics.conversationId,
  conversations: state.codiAnalytics.conversations,
  conversationsLoading: state.codiAnalytics.conversationsLoading,
  preferredTimezone: state.authorization?.user?.preferredTimezone,
});

const mapDispatchToProps = {
  askCodiAnalytics,
  startNewCodiAnalyticsChat,
  loadCodiConversations,
  openCodiConversation,
  deleteCodiConversation,
};

export default connect(mapStateToProps, mapDispatchToProps)(Codi_Analytics);
